import Box from '@mui/material/Box';
import { INK } from 'client/modules/Analyze/analyzeTheme';
import ChartTooltip from 'client/modules/Analyze/components/ChartTooltip';
import PropTypes from 'prop-types';
import React, { useState } from 'react';

const W = 1180;
const H = 360;
const PAD_L = 46;
const PAD_R = 28;
const PAD_T = 34;
const PAD_B = 40;
const PW = W - PAD_L - PAD_R;
const PH = H - PAD_T - PAD_B;
const Y_TOP = PAD_T;
const Y_BOT = PAD_T + PH;

// Stacked group composition bars with the score line overlaid, plus a hover
// tooltip showing each group's share/volume, the score, and total responses.
function OverTimeCompositionChart({ buckets, scoreLabel, formatNumber }) {
  const [active, setActive] = useState(null);
  const n = buckets.length;
  if (n === 0) {
    return null;
  }
  const slot = PW / n;
  const bw = Math.min(70, slot * 0.5);
  const centerX = i => PAD_L + slot * i + slot / 2;

  const npsValues = buckets.map(b => b.nps);
  const lo = Math.min(...npsValues) - 10;
  const hi = Math.max(...npsValues) + 12;
  const span = hi - lo || 1;
  const npsY = v => Y_TOP + PH * (1 - (v - lo) / span);
  const points = buckets.map((b, i) => [centerX(i), npsY(b.nps)]);
  const linePath = `M${points.map(p => `${p[0]},${p[1]}`).join(' L')}`;

  const activeBucket = active != null ? buckets[active] : null;

  return (
    <Box sx={{ position: 'relative' }} onMouseLeave={() => setActive(null)}>
      <Box
        component="svg"
        viewBox={`0 0 ${W} ${H}`}
        sx={{ width: '100%', height: 'auto', display: 'block' }}
      >
        {[0, 50, 100].map(p => {
          const y = Y_TOP + PH * (1 - p / 100);
          return (
            <g key={`grid-${p}`}>
              <line x1={PAD_L} y1={y} x2={W - PAD_R} y2={y} stroke="#EEF1F5" strokeWidth={1} />
              <text
                x={PAD_L - 12}
                y={y + 4}
                textAnchor="end"
                fontSize={12}
                fill="#A6AFBD"
                fontFamily="Figtree"
              >
                {p}%
              </text>
            </g>
          );
        })}

        {active != null && (
          <line
            x1={centerX(active)}
            y1={Y_TOP}
            x2={centerX(active)}
            y2={Y_BOT}
            stroke="#D6DEE8"
            strokeWidth={1}
            strokeDasharray="4 4"
          />
        )}

        {buckets.map((bucket, i) => {
          const x = centerX(i) - bw / 2;
          let y = Y_TOP;
          return (
            <g key={`bar-${bucket.label}-${i}`}>
              {bucket.segments.map(seg => {
                const segH = PH * ((seg.pct || 0) / 100);
                const rectY = y + 1;
                y += segH;
                return (
                  <rect
                    key={seg.label}
                    x={x}
                    y={rectY}
                    width={bw}
                    height={Math.max(0, segH - 2)}
                    rx={2}
                    fill={seg.color}
                    opacity={active == null || active === i ? 1 : 0.45}
                  />
                );
              })}
              <text
                x={centerX(i)}
                y={Y_BOT + 24}
                textAnchor="middle"
                fontSize={13}
                fill="#5B6675"
                fontWeight={600}
                fontFamily="Figtree"
              >
                {bucket.label}
              </text>
            </g>
          );
        })}

        <path d={linePath} fill="none" stroke={INK} strokeWidth={2.5} strokeLinejoin="round" />
        {points.map((p, i) => (
          <g key={`pt-${i}`}>
            <circle
              cx={p[0]}
              cy={p[1]}
              r={active === i ? 5.5 : 4.5}
              fill="#fff"
              stroke={INK}
              strokeWidth={2}
            />
            <text
              x={p[0]}
              y={p[1] - 13}
              textAnchor="middle"
              fontSize={13}
              fontWeight={700}
              fill={INK}
              fontFamily="Figtree"
            >
              {formatNumber(buckets[i].nps)}
            </text>
          </g>
        ))}

        {/* hover hit areas */}
        {buckets.map((bucket, i) => (
          <rect
            key={`hit-${i}`}
            x={PAD_L + slot * i}
            y={Y_TOP}
            width={slot}
            height={PH}
            fill="transparent"
            onMouseEnter={() => setActive(i)}
          />
        ))}
      </Box>

      {activeBucket && (
        <ChartTooltip
          xPct={(centerX(active) / W) * 100}
          title={activeBucket.label}
          rows={[
            ...activeBucket.segments.map(seg => ({
              label: seg.label,
              color: seg.color,
              value: `${formatNumber(seg.pct)}%`,
              sub: formatNumber(seg.count),
            })),
            { label: scoreLabel, value: formatNumber(activeBucket.nps) },
          ]}
          footer={{ label: 'Responses', value: formatNumber(activeBucket.total) }}
        />
      )}
    </Box>
  );
}

OverTimeCompositionChart.propTypes = {
  buckets: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      nps: PropTypes.number,
      total: PropTypes.number,
      segments: PropTypes.arrayOf(
        PropTypes.shape({
          label: PropTypes.string.isRequired,
          color: PropTypes.string.isRequired,
          pct: PropTypes.number,
          count: PropTypes.number,
        }),
      ).isRequired,
    }),
  ).isRequired,
  scoreLabel: PropTypes.string.isRequired,
  formatNumber: PropTypes.func.isRequired,
};

export default OverTimeCompositionChart;
